import jsPDF from "jspdf"
import "resource/font/THSarabun-normal"
import "resource/font/THSarabun-bold"
import dayjs from "dayjs"
import { alertError } from "components/Alert"
import { printHeader } from "./pirntHeader"
import { printTopRight } from "./pirntTopRight"
import { printLineSigns } from "utils/printLineSigns"

const PAGE = [8.3, 11.7] //A4
const START_X = 0.5
const START_Y = 0.4
const LINE_HEIGHT = 0.25
const CONTENT_SIZE = 14
const CONTENT_WIDTH = PAGE[0] - START_X * 2   // 7.3
const MAX_Y = PAGE[1] - 0.5

// Label column widths of the two-up detail rows, and where the right pair starts.
const LABEL_WIDTH = 1.15
const RIGHT_COL_X = START_X + 3.9

// Fixed y of the กำหนดเวลา row. Everything above it is sized to land here, which
// keeps the signature block at the same height on every sheet.
const TIMING_Y = 8.9
const ORD_DSCP_HEIGHT = 1.6

// รูปภาพ are stored per repair stage - same ROStsId the upload dialog tags them with.
const IMAGE_SECTIONS = [
  { ROStsId: 1, label: "รูปแจ้งซ่อม" },
  { ROStsId: 2, label: "รูปการซ่อม" },
  { ROStsId: 3, label: "รูปหลังซ่อม" },
]
const IMG_COLS = 3
const IMG_GAP = 0.2
const IMG_BOX_W = (CONTENT_WIDTH - IMG_GAP * (IMG_COLS - 1)) / IMG_COLS
const IMG_BOX_H = IMG_BOX_W * 3 / 4

/**
 * Loads a (possibly cross-origin) image URL as a JPEG data URL with its natural
 * size. Resolves null on any failure (deleted file / CORS) so the document still
 * prints without that photo instead of failing outright.
 */
const loadImage = (url) => new Promise((resolve) => {
  if (!url) return resolve(null)
  const img = new Image()
  img.crossOrigin = "anonymous"
  img.onload = () => {
    try {
      const canvas = document.createElement("canvas")
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight
      canvas.getContext("2d").drawImage(img, 0, 0)
      resolve({ dataUrl: canvas.toDataURL("image/jpeg", 0.85), w: img.naturalWidth, h: img.naturalHeight })
    } catch (e) {
      resolve(null)
    }
  }
  img.onerror = () => resolve(null)
  img.src = url
})

const fmtDteTm = (value) => value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "-"
const dash = (value) => (value === null || value === undefined || value === "" ? "-" : String(value))

/**
 * The grid rows carry the joined display names, the dialog's row does not (it comes
 * from `select * from repair_order`), so every name is resolved from msData as a
 * fallback. Keeps one row shape for both callers.
 */
const resolveRow = (row, msData) => ({
  ...row,
  RONo: row.RONo || "-",
  ROStsNm: row.ROStsNm
    || msData.repairOrderStatuses?.find(item => item.ROStsId === row.ROStsId)?.ROStsNm
    || "-",
  DrvNNm: row.DrvNNm || msData.driverComboObj?.[row.DrvId]?.label || "-",
  TukCd: row.TukCd || msData.truckObj?.[row.TukId]?.Code || "-",
  ReprNNm: row.ReprNNm
    || (row.ReprAccId ? msData.accountObj?.[row.ReprAccId]?.NName : null)
    || (row.ReprSupId ? msData.supplierCombo?.find(item => item.id === row.ReprSupId)?.label : null)
    || row.ReprOthNm
    || "-",
})

/** Bold heading with a rule under it, spanning the content width. */
const sectionTitle = (doc, label, yPos) => {
  doc.setFont("THSarabun", "bold")
  doc.setFontSize(CONTENT_SIZE)
  doc.text(label, START_X, yPos)
  doc.line(START_X, yPos + 0.07, START_X + CONTENT_WIDTH, yPos + 0.07)
  return yPos + LINE_HEIGHT * 1.2
}

/** `label : value` on one line. Returns nothing - rows here are a fixed height. */
const labelValue = (doc, label, value, xPos, yPos, labelWidth = LABEL_WIDTH) => {
  doc.setFont("THSarabun", "bold")
  doc.setFontSize(CONTENT_SIZE)
  doc.text(`${label} :`, xPos, yPos)
  doc.setFont("THSarabun", "normal")
  doc.text(dash(value), xPos + labelWidth, yPos)
}

/**
 * Framed free-text block. It keeps at least `minHeight` whether or not anything
 * was written in it, so every ใบแจ้งซ่อม has the same shape and there is room to
 * write on the printed sheet; longer text grows the box instead of being cut.
 */
const textBox = (doc, text, yPos, minHeight) => {
  doc.setFont("THSarabun", "normal")
  doc.setFontSize(CONTENT_SIZE)
  const lines = text ? doc.splitTextToSize(String(text), CONTENT_WIDTH - 0.2) : []
  const boxHeight = Math.max(lines.length * LINE_HEIGHT + 0.1, minHeight)
  doc.rect(START_X, yPos, CONTENT_WIDTH, boxHeight)
  let textY = yPos + LINE_HEIGHT
  for (const line of lines) {
    doc.text(line, START_X + 0.1, textY)
    textY += LINE_HEIGHT
  }
  return yPos + boxHeight
}

/** Page 1 of one repair order: company header, document info, details, signatures. */
const printDetailPage = (doc, row, companyData) => {
  const titleObj = { label: "ใบแจ้งซ่อม", xPos: 6.3, align: "center", size: 22 }
  const infoObj = {
    xPos: 4.9,
    objs: [
      { header: "เลขที่แจ้งซ่อม :", value: row.RONo, headerWidth: 1.1 },
      { header: "วันที่แจ้งซ่อม :", value: fmtDteTm(row.ROTm), headerWidth: 1.1 },
      { header: "สถานะการซ่อม :", value: row.ROStsNm, headerWidth: 1.1 },
    ],
  }
  const yRight = printTopRight({ jsPDFDoc: doc, titleObj, infoObj, startY: START_Y + 0.2, contentSize: CONTENT_SIZE })
  const yLeft = printHeader({ jsPDFDoc: doc, companyData, startX: START_X, startY: START_Y })
  let yPos = Math.max(yLeft, yRight) + LINE_HEIGHT

  yPos = sectionTitle(doc, "ข้อมูลรถและผู้ซ่อม", yPos)
  labelValue(doc, "พนักงานขับรถ", row.DrvNNm, START_X, yPos)
  labelValue(doc, "เลขรถ", row.TukCd, RIGHT_COL_X, yPos, 0.6)
  yPos += LINE_HEIGHT
  labelValue(doc, "ผู้ดำเนินการซ่อม", row.ReprNNm, START_X, yPos)
  yPos += LINE_HEIGHT * 1.6

  yPos = sectionTitle(doc, "รายละเอียดการแจ้งซ่อม", yPos)
  labelValue(doc, "หัวข้อแจ้งซ่อม", row.OrdHdr, START_X, yPos)
  yPos += LINE_HEIGHT * 1.2
  doc.setFont("THSarabun", "bold")
  doc.text("รายละเอียดแจ้งซ่อม", START_X, yPos)
  yPos = textBox(doc, row.OrdDscp, yPos + 0.08, ORD_DSCP_HEIGHT)
  yPos += LINE_HEIGHT * 1.4

  yPos = sectionTitle(doc, "ผลการซ่อม", yPos)
  doc.setFont("THSarabun", "bold")
  doc.text("รายละเอียดการซ่อม", START_X, yPos)
  // Stretch this box down to TIMING_Y so the form fills the sheet instead of
  // leaving a band of white above the signatures.
  const reprBoxTop = yPos + 0.08
  yPos = textBox(doc, row.ReprDscp, reprBoxTop, TIMING_Y - LINE_HEIGHT * 1.4 - reprBoxTop)
  yPos += LINE_HEIGHT * 1.4

  yPos = sectionTitle(doc, "กำหนดเวลา", yPos)
  const dateColWidth = CONTENT_WIDTH / 3
  labelValue(doc, "วันที่เริ่มซ่อม", fmtDteTm(row.StDte), START_X, yPos, 1.0)
  labelValue(doc, "วันที่คาดว่าเสร็จ", fmtDteTm(row.EstFinDte), START_X + dateColWidth, yPos, 1.2)
  labelValue(doc, "วันที่เสร็จ", fmtDteTm(row.FinDte), START_X + dateColWidth * 2, yPos, 0.85)

  // Signatures sit at the foot of the form so every ใบแจ้งซ่อม signs in the same
  // place; an unusually long description pushes them down rather than under it.
  printLineSigns({
    doc,
    startY: Math.max(PAGE[1] - 1.1, yPos + LINE_HEIGHT * 3),
    labels: ["ผู้แจ้งซ่อม", "ผู้ดำเนินการซ่อม", "ผู้ตรวจรับงาน"],
    labels2: ["( " + row.DrvNNm + " )", "( " + row.ReprNNm + " )", "(                              )"],
    pageWidth: PAGE[0],
    paddingPage: START_X,
    paddingCol: 0.35,
    lineHeight: LINE_HEIGHT,
    contentSize: CONTENT_SIZE,
  })
}

/** Small self-identifying header for the photo pages that follow the form. */
const printImagePageHeader = (doc, row) => {
  doc.setFont("THSarabun", "bold")
  doc.setFontSize(16)
  doc.text(`ใบแจ้งซ่อมเลขที่ ${row.RONo} - รูปภาพประกอบ`, START_X, START_Y + 0.1)
  doc.setFontSize(CONTENT_SIZE)
  doc.setFont("THSarabun", "normal")
  doc.text(`${row.TukCd}  ${row.DrvNNm}  ${fmtDteTm(row.ROTm)}`, START_X + CONTENT_WIDTH, START_Y + 0.1, { align: "right" })
  doc.line(START_X, START_Y + 0.2, START_X + CONTENT_WIDTH, START_Y + 0.2)
  return START_Y + 0.2 + LINE_HEIGHT * 1.4
}

/**
 * Photo pages for one repair order, grouped by stage. Only photos that actually
 * loaded are drawn, and a stage with none is skipped entirely.
 */
const printImagePages = async (doc, row) => {
  const files = row.Files || []
  const sections = []
  for (const section of IMAGE_SECTIONS) {
    const sectionFiles = files.filter(file => file.ROStsId === section.ROStsId && file.imgLink)
    const images = (await Promise.all(sectionFiles.map(async (file) => ({
      label: file.Label || "",
      img: await loadImage(file.imgLink),
    })))).filter(item => item.img)
    if (images.length > 0) sections.push({ label: section.label, images })
  }
  if (sections.length === 0) return false

  doc.addPage(PAGE)
  let yPos = printImagePageHeader(doc, row)

  for (const section of sections) {
    if (yPos + LINE_HEIGHT * 2 + IMG_BOX_H > MAX_Y) {
      doc.addPage(PAGE)
      yPos = printImagePageHeader(doc, row)
    }
    yPos = sectionTitle(doc, `${section.label} (${section.images.length} รูป)`, yPos)

    for (let i = 0; i < section.images.length; i++) {
      const col = i % IMG_COLS
      if (col === 0 && i > 0) {
        yPos += IMG_BOX_H + LINE_HEIGHT * 1.2
        if (yPos + IMG_BOX_H + LINE_HEIGHT > MAX_Y) {
          doc.addPage(PAGE)
          yPos = printImagePageHeader(doc, row)
          yPos = sectionTitle(doc, `${section.label} (ต่อ)`, yPos)
        }
      }
      const { label, img } = section.images[i]
      const boxX = START_X + col * (IMG_BOX_W + IMG_GAP)
      doc.rect(boxX, yPos, IMG_BOX_W, IMG_BOX_H)
      const scale = Math.min((IMG_BOX_W - 0.06) / img.w, (IMG_BOX_H - 0.06) / img.h)
      doc.addImage(img.dataUrl, "JPEG",
        boxX + (IMG_BOX_W - img.w * scale) / 2,
        yPos + (IMG_BOX_H - img.h * scale) / 2,
        img.w * scale, img.h * scale)
      if (label) {
        doc.setFont("THSarabun", "normal")
        doc.setFontSize(11)
        doc.text(label, boxX + IMG_BOX_W / 2, yPos + IMG_BOX_H + 0.16, { align: "center", maxWidth: IMG_BOX_W })
      }
    }
    yPos += IMG_BOX_H + LINE_HEIGHT * 1.6
  }
  return true
}

/**
 * ใบแจ้งซ่อม - one A4 form per repair order, printed from the dialog (one row) or
 * from the checked rows of the grid. With `withImages` each order's photos follow
 * its form on their own pages, so the form itself never shifts.
 *
 * `rows` are repair_order rows; for withImages they must carry `Files`
 * (ROStsId + imgLink), i.e. what getRepairOrderDetail returns.
 */
export const printRepairOrder = async ({ msData, rows, withImages = false }) => {
  if (!rows || rows.length === 0) {
    alertError("ไม่มีข้อมูลสำหรับพิมพ์")
    return
  }
  const companyData = msData.serverData.CompanyData
  const doc = new jsPDF({ orientation: "portrait", unit: "in", format: PAGE })
  doc.setLineWidth(0.01)
  doc.setFont("THSarabun")

  for (let i = 0; i < rows.length; i++) {
    if (i > 0) doc.addPage(PAGE)
    const row = resolveRow(rows[i], msData)
    printDetailPage(doc, row, companyData)
    if (withImages) await printImagePages(doc, row)
  }

  const blob = new Blob([doc.output("blob")], { type: "application/pdf" })
  window.open(URL.createObjectURL(blob), "_blank")
}
